import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useCurrentAppUserId } from '@/hooks/useCurrentAppUser';

interface Note {
  id: string;
  case_id?: string;
  contact_id?: string;
  deal_id?: string;
  title?: string;
  content: string;
  created_at: string;
  created_by?: string;
  modified_at?: string;
  modified_by?: string;
  creator?: {
    display_name: string;
    avatar_url?: string;
  };
}

export function useCaseNotes(caseId: string | null, contactIds: string[] = []) {
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const currentAppUserId = useCurrentAppUserId();

  const loadNotes = async () => {
    if (!caseId) {
      setNotes([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Build query to fetch notes by case_id OR contact_ids
      // This allows showing notes from related contacts even if not linked to case_id
      const conditions = [`case_id.eq.${caseId}`];
      
      if (contactIds.length > 0) {
        conditions.push(`contact_id.in.(${contactIds.join(',')})`);
      }

      const { data, error: fetchError } = await supabase
        .from('notes')
        .select(`
          *,
          creator:app_users!notes_created_by_fkey(display_name, avatar_url)
        `)
        .or(conditions.join(','))
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setNotes(data || []);
    } catch (err) {
      console.error('Error loading case notes:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  };

  const createNote = async (noteData: { title?: string; content: string }) => {
    if (!caseId) throw new Error('Case ID is required');
    
    try {
      const newNote = {
        ...noteData,
        case_id: caseId,
        created_by: currentAppUserId || null,
      };

      const { data, error } = await supabase
        .from('notes')
        .insert(newNote)
        .select()
        .single();

      if (error) throw error;
      await loadNotes();
      return data;
    } catch (err) {
      console.error('Error creating note:', err);
      toast.error('Failed to create note');
      throw err;
    }
  };

  const deleteNote = async (id: string) => {
    try {
      const { error } = await supabase
        .from('notes')
        .delete()
        .eq('id', id);

      if (error) throw error;
      await loadNotes();
    } catch (err) {
      console.error('Error deleting note:', err);
      toast.error('Failed to delete note');
      throw err;
    }
  };

  useEffect(() => {
    loadNotes();
  }, [caseId, contactIds.join(',')]);

  return {
    notes,
    loading,
    error,
    loadNotes,
    createNote,
    deleteNote,
  };
}
